import React, { Suspense, lazy, useState, useEffect } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { useSearchParams } from "react-router-dom";
import { useChatHistory } from "@/hooks/useChatHistory";
import axios from "axios";
import { toast } from "sonner";
import DashboardHeader from "@/components/DashboardHeader";
import CreditBadge from "@/components/CreditBadge";  // iter1118
import SEO from "@/components/SEO";
import UpgradeLock, { useSubscriptionAccess } from "@/components/UpgradeLock";
import AnalysesGuide from "@/components/guides/AnalysesGuide";
import ErrorBoundary from "@/components/ErrorBoundary";
import SaveToPmosButton from "@/components/womens_health/SaveToPmosButton";
import HowToStartCard from "@/components/HowToStartCard";  // iter504 — guided intro
import DashboardWidget from "@/dashboard/DashboardWidget";  // iter553 — customizable visibility
import { Loader2, Activity, FlaskConical, Dna, ScanLine, Network, MessageSquareText } from "lucide-react";

const ClinicalAnalysis = lazy(() => import("@/components/ClinicalAnalysis"));
const HealthIndices = lazy(() => import("@/components/HealthIndices"));
const LabUploadAnalyzer = lazy(() => import("@/components/LabUploadAnalyzer"));
const Nutrigenomics = lazy(() => import("@/components/Nutrigenomics"));
const MedicalImageScreening = lazy(() => import("@/components/MedicalImageScreening"));
const PhenotypeClassifier = lazy(() => import("@/components/PhenotypeClassifier"));
const PhenotypeSynergyPanel = lazy(() => import("@/components/PhenotypeSynergyPanel"));
const BiologicalAgePanel = lazy(() => import("@/components/BiologicalAgePanel"));
const LongevityScorePanel = lazy(() => import("@/components/LongevityScorePanel"));
const NephrologyPanel = lazy(() => import("@/components/NephrologyPanel"));
const NutrientDeficiencyPanel = lazy(() => import("@/components/NutrientDeficiencyPanel"));
const DietaryPatternsCard = lazy(() => import("@/components/DietaryPatternsCard"));
const EpigeneticMapPanel = lazy(() => import("@/components/EpigeneticMapPanel"));
const HealthCalculators = lazy(() => import("@/components/HealthCalculators"));
const PathwayCoverageCard = lazy(() => import("@/components/PathwayCoverageCard"));
const MealComplianceWidget = lazy(() => import("@/components/MealComplianceWidget"));
const MindBodyRecommender = lazy(() => import("@/components/MindBodyRecommender"));
const VisceralAdiposityCard = lazy(() => import("@/components/VisceralAdiposityCard"));
const VAISReductionPathwayPanel = lazy(() => import("@/components/VAISReductionPathwayPanel"));
const ChatInterface = lazy(() => import("@/components/ChatInterface"));
const AIInsightsPanel = lazy(() => import("@/components/AIInsightsPanel"));
const ChatHistoryList = lazy(() => import("@/components/ChatHistoryList"));

// iter222 — пренареден ред на табовете по заявка на потребителя:
// Лаборатории → Епигенетична карта → Генетика → AI Образна диагностика
// → Клиничен анализ → Nutramilo AI (нов таб с 5-те AI асистента)
const TABS = [
  { key: "lab",       icon: FlaskConical,     label_bg: "Лаборатории",         label_en: "Lab" },
  { key: "epimap",    icon: Network,          label_bg: "Епигенетична карта",  label_en: "Epigenetic Map" },
  { key: "genetic",   icon: Dna,              label_bg: "Генетика",            label_en: "Genetic" },
  { key: "image",     icon: ScanLine,         label_bg: "AI Образна диагностика", label_en: "AI Imaging" },
  { key: "clinical",  icon: Activity,         label_bg: "Клиничен анализ",     label_en: "Clinical" },
  { key: "nutramilo_ai", icon: MessageSquareText, label_bg: "Nutramilo AI",    label_en: "Nutramilo AI" },
];

/**
 * Analyses Page — Phase 3: tabbed view for Clinical / Lab / Genetic / Image.
 * URL-synced via ?tab= query param, так че линкове deep-link работят.
 */
export default function AnalysesPage() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = TABS.find(t => t.key === searchParams.get("tab"))?.key || "lab";
  const [tab, setTab] = useState(initialTab);
  const access = useSubscriptionAccess();

  // iter222 — Nutramilo AI tab състояние (reuse-ва /api/query/execute endpoint
  // и chat history hook от Dashboard за идентичен AI flow).
  const [aiQuery, setAiQuery] = useState("");
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const { history: chatHistory, addEntry: addChatEntry, removeEntry: removeChatEntry, clearHistory: clearChatHistory } = useChatHistory();

  const handleAskAI = async () => {
    if (!aiQuery.trim()) {
      toast.error(bg ? "Въведете въпрос" : "Enter a question");
      return;
    }
    setAiLoading(true);
    try {
      const { data } = await axios.post(`${API}/query/execute`, { query: aiQuery, language });
      setAiResult(data);
      addChatEntry({ query: aiQuery, result: data, timestamp: new Date().toISOString() });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402) {
        toast.error(bg ? "Недостатъчно кредити" : "Not enough credits");
      } else {
        toast.error(detail || (bg ? "AI грешка" : "AI error"));
      }
    } finally {
      setAiLoading(false);
    }
  };

  // Sync ?tab= query param when user switches tabs.
  useEffect(() => {
    const current = searchParams.get("tab");
    if (tab !== current) {
      const next = new URLSearchParams(searchParams);
      if (tab === "lab") next.delete("tab"); else next.set("tab", tab);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const tabBaseCls = "inline-flex items-center gap-1.5 px-4 h-10 rounded-xl text-sm font-semibold transition-all whitespace-nowrap";
  const tabActiveCls = isDark
    ? "bg-sky-500/20 text-sky-300 shadow-sm shadow-sky-900/20"
    : "bg-sky-600 text-white shadow-sm shadow-sky-600/20";
  const tabIdleCls = isDark
    ? "text-gray-400 hover:text-gray-200 hover:bg-white/5"
    : "text-gray-600 hover:text-gray-900 hover:bg-gray-100";

  return (
    <div className={`min-h-screen ${isDark ? "bg-[hsl(222,47%,6%)] text-white" : "bg-[#F8FAFC] text-gray-900"}`}
      data-testid="analyses-page">
      <SEO
        title={bg ? "Анализи — Nutramilo" : "Analyses — Nutramilo"}
        description={bg ? "Клиничен AI анализ, лабораторни, генетични, изобразителни и риск-калкулатори на едно място."
          : "Clinical AI analysis, lab, genetic, imaging, and risk calculators in one place."}
        path="/dashboard/analyses"
      />
      <DashboardHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        <header className="mb-4">
          <h1 className="text-xl sm:text-2xl font-bold">
            {bg ? "Задълбочени анализи" : "Deep Analyses"}
          </h1>
          <p className={`text-sm mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
            {bg ? "Клиничен AI, лаборатории, генетика и изображения — всички на едно място."
              : "Clinical AI, labs, genetics and imaging — all in one place."}
          </p>
        </header>

        {/* iter504 — How to start (collapsible, consistent across complex pages) */}
        <HowToStartCard
          storageKey="nutramilo_howto_precision_health"
          isDark={isDark}
          accent="from-fuchsia-500 to-violet-500"
          testid="howto-precision-health"
          title={bg ? "Как да започнете с прецизното здраве" : "How to start with Precision Health"}
          subtitle={bg
            ? "Качете едно лабораторно изследване → извеждаме Вашите клинични показатели."
            : "Upload 1 lab → AI derives your clinical indices."}
          benefits={bg
            ? ["Разчитане на документ или снимка", "Клинични показатели", "Определяне на обменния тип", "Биологична възраст"]
            : ["AI OCR on PDF/photo", "Clinical indices", "Phenotype Classifier", "Biological age"]}
          steps={bg ? [
            { title: "1. Започнете с клиничното разчитане",
              body: "Вижте VAIS, COL3, NRF и обменните показатели, изчислени от вече въведените Ви данни." },
            { title: "2. Качете лабораторно изследване",
              body: "Документ или снимка — разчитаме я сами и попълваме хормоните и показателите (HOMA-IR, HbA1c, CRP)." },
            { title: "3. Вижте обменния си тип",
              body: "Извеждаме подтипа на обмяната Ви: воден от инсулина, възпалителен или от страна на митохондриите." },
            { title: "4. Открийте биологичната си възраст",
              body: "Колко е действителната Ви възраст по PhenoAge и Klemera-Doubal, на основата на изследванията." },
          ] : [
            { title: "1. Start with Clinical AI",
              body: "See VAIS, COL3, NRF and metabolic indices computed from your existing data." },
            { title: "2. Upload a lab",
              body: "PDF or photo — AI reads it automatically and fills hormones/markers (HOMA-IR, HbA1c, CRP)." },
            { title: "3. Check Phenotype Classifier",
              body: "AI derives your metabolic subtype (insulin-driven / inflammatory / mitochondrial)." },
            { title: "4. Discover Biological Age",
              body: "Your real age via PhenoAge / Klemera-Doubal, based on your labs." },
          ]}
        />

        {/* Sub-tab bar — horizontal scroll on mobile */}
        <div
          className={`sticky top-[64px] z-20 -mx-4 sm:-mx-6 px-4 sm:px-6 py-2 mb-4 border-b backdrop-blur
            ${isDark ? "bg-[hsl(222,47%,6%)]/85 border-white/5" : "bg-[#F8FAFC]/90 border-gray-200"}`}
          data-testid="analyses-subtabs"
        >
          <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`${tabBaseCls} ${active ? tabActiveCls : tabIdleCls}`}
                  data-testid={`analyses-tab-${t.key}`}
                  aria-pressed={active}
                >
                  <Icon className="w-4 h-4" />
                  {bg ? t.label_bg : t.label_en}
                </button>
              );
            })}
          </div>
        </div>

        <Suspense fallback={<div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>}>
          {tab === "clinical" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5 animate-fade-in-up" data-testid="analyses-tab-content-clinical">
              <div className="space-y-4">
                {access.canClinicalAnalysis ? <ClinicalAnalysis /> : <UpgradeLock feature="clinical_analysis" />}
                {/* iter380 — VAIS пълна карта + Reduction Pathway преместени тук
                    по заявка на потребителя (от Dashboard). Patient-facing,
                    с info модал и MDR disclaimer. */}
                <div id="vais" className="scroll-mt-20 space-y-4" data-testid="analyses-vais-section">
                  <VisceralAdiposityCard />
                  <VAISReductionPathwayPanel />
                </div>
                {/* iter222 — BreastCancerRiskPanel преместен в "AI Образна диагностика" таб (там се изчислява плътността). */}
                {/* iter222 — DrugFoodInteractions скрит по заявка на потребителя. */}
                {/* iter221 — NutrientDeficiencyPanel преместен в Diary под Хранителни модели. */}
              </div>
              <div className="space-y-4">
                {access.canClinicalPhenotypes ? <PhenotypeClassifier /> : <UpgradeLock feature="clinical_phenotypes" />}
                {access.canPhenotypeSynergy ? <PhenotypeSynergyPanel /> : null /* iter310 — Synergy hidden for non-clinicians; primary notice is already in clinical_phenotypes lock above */}
                {/* iter315 — От премахнатия План menu, поставени СЛЕД Споделени
                    KEGG pathways (PhenotypeSynergyPanel) по заявка на потребителя.
                    iter380 — gated за лекари (MDR Rule 11). */}
                {access.canPhenotypePathways ? (
                  <>
                    <PathwayCoverageCard />
                    <MealComplianceWidget />
                  </>
                ) : <UpgradeLock feature="phenotype_pathways" />}
                {access.canMindBody ? <MindBodyRecommender /> : <UpgradeLock feature="mind_body" />}
                {/* iter221 — HemoBot скрит по заявка на потребителя. */}
                {/* iter222 — TyrerCuzickPanel премахнат (BreastCancerRiskPanel включва  + Gail в AI Imaging таб). */}
                {access.canNephrologyCalculators ? <NephrologyPanel /> : <UpgradeLock feature="nephrology_calculators" />}
                {/* iter314 — Здравни калкулатори преместени тук от План/Навици. */}
                <HealthCalculators />
              </div>
            </div>
          )}

          {tab === "epimap" && (
            <div className="grid grid-cols-1 gap-4 sm:gap-5 animate-fade-in-up" data-testid="analyses-tab-content-epimap">
              {access.canEpigeneticMap ? <EpigeneticMapPanel /> : <UpgradeLock feature="epigenetic_map" />}
            </div>
          )}

          {tab === "lab" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5 animate-fade-in-up" data-testid="analyses-tab-content-lab">
              <div className="space-y-4">
                <LabUploadAnalyzer />
                <div className="flex justify-end">
                  <SaveToPmosButton
                    module="health_intelligence"
                    bg={bg}
                    payload={{ source: "analyses_lab", snapshot_date: new Date().toISOString().slice(0, 10) }}
                    note="Lab analyses snapshot"
                    testId="analyses-lab-save-to-pmos"
                  />
                </div>
              </div>
              <div className="space-y-4">
                {/* iter315 — Биологична възраст + Longevity Score преместени
                    от Клиничен анализ → Лаборатория по заявка на потребителя.
                    Те се изчисляват от лабораторни биомаркери (HbA1c, hsCRP, GFR, ...). */}
                {access.canBiologicalAge ? (
                  <ErrorBoundary label={bg ? "Биологична възраст" : "Biological Age"}><BiologicalAgePanel /></ErrorBoundary>
                ) : <UpgradeLock feature="biological_age" />}
                {access.canLongevity ? (
                  <ErrorBoundary label="Longevity Score"><LongevityScorePanel /></ErrorBoundary>
                ) : <UpgradeLock feature="longevity" />}
              </div>
            </div>
          )}

          {tab === "genetic" && (
            <div className="grid grid-cols-1 gap-4 sm:gap-5 animate-fade-in-up" data-testid="analyses-tab-content-genetic">
              <Nutrigenomics />
            </div>
          )}

          {tab === "image" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5 animate-fade-in-up" data-testid="analyses-tab-content-image">
              <MedicalImageScreening />
              {/* iter538 — BreastCancerRiskPanel/ clinical version
                  removed from Precision Health → AI Imaging tab (MDR Rule 11
                  exposure). The standard registered users access 
                  via the dedicated public /breast-health page (BreastHealthHub)
                  which is properly disclaimer-gated as an educational tool. */}
              <div className="lg:col-span-2 flex justify-end">
                <SaveToPmosButton
                  module="health_intelligence"
                  bg={bg}
                  payload={{ source: "analyses_image", snapshot_date: new Date().toISOString().slice(0, 10) }}
                  note="AI Imaging snapshot"
                  testId="analyses-image-save-to-pmos"
                />
              </div>
            </div>
          )}

          {tab === "nutramilo_ai" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5 animate-fade-in-up" data-testid="analyses-tab-content-nutramilo-ai">
              <div className="lg:col-span-2 space-y-4">
                {/* iter1118 — индикатор, че запитването ползва кредити */}
                <div className="flex items-center justify-end gap-2">
                  <span className="text-[11px] text-gray-500">Всяко AI запитване:</span>
                  <CreditBadge featureId="ai_question" fallbackCost={1} />
                </div>
                <ChatInterface
                  query={aiQuery}
                  setQuery={setAiQuery}
                  onSubmit={handleAskAI}
                  loading={aiLoading}
                  result={aiResult}
                />
                {aiResult?.final_response && <AIInsightsPanel result={aiResult} />}
              </div>
              <div className="space-y-4">
                <ChatHistoryList
                  history={chatHistory}
                  onSelect={(entry) => { setAiQuery(entry.query); setAiResult(entry.result); }}
                  onRemove={removeChatEntry}
                  onClear={clearChatHistory}
                />
              </div>
            </div>
          )}
        </Suspense>
      </main>
      <AnalysesGuide />
    </div>
  );
}
